import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Feedback';
import { Pill } from '@/components/ui/Pill';
import { useToast } from '@/components/ui/Toast';
import { Dialog } from '@/components/ui/Dialog';
import { StatBlock, StatRow } from '@/components/ui/StatBlock';
import { groupRoundsApi, roundsApi, usersApi } from '@/lib/api/endpoints';
import {
  courseNameFrom,
  inProgressRounds,
  qk,
  useAllRounds,
  useCourse,
  useCourseIndex,
  useGroupRound,
  useGroupRoundByRound,
  useRound,
} from '@/lib/api/queries';
import type { CourseSummary, Round, TeeBox } from '@/lib/api/types';
import { useCurrentUser } from '@/lib/auth/AuthContext';
import { cx } from '@/lib/cx';
import { fmtDecimal, fmtInt, fmtPct, fmtToPar } from '@/lib/format/number';
import { fromCoordinates, haversineMeters } from '@/lib/geo/geoPoint';
import { fmtCourseDistance, fmtDistance, loadUnit } from '@/lib/geo/units';
import { useGeolocation } from '@/lib/geo/useGeolocation';
import { routes } from '@/routes/paths';
import { teeColor } from './teeColor';
import { HoleEntry } from './HoleEntry';
import { GroupScorecard } from './GroupScorecard';
import { StepPlayers } from './StepPlayers';
import { collator, t, tx } from '@/lib/i18n';
import styles from './Record.module.css';
import { CheckIcon } from '@/components/icons';
import { ChevronLeftIcon, CloseIcon, SearchIcon } from '@/components/icons';

/**
 * O passo de jogadores (canvas 1r) entra **entre a configuração e o scorecard**, e é ele que faz
 * o fluxo passar de 4 para 5 passos. Ele aparece sempre, porque é onde você diz se jogou sozinho
 * — a decisão não existe antes de alguém tomá-la. Sem ninguém no flight, os passos 4 e 5 são
 * exatamente os de antes.
 */
type Step = 1 | 2 | 3 | 4 | 5;

const TOTAL_STEPS = 5;

/** O que o passo 2 decidiu, guardado até o passo 3 saber se a volta é individual ou de flight. */
interface RoundConfig {
  courseId: string;
  teeBoxName: string;
  teeBoxNameBack: string | null;
  holesPlayed: 9 | 18;
}

export function RecordRound() {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const allRounds = useAllRounds();
  const courseIndex = useCourseIndex();

  /*
   * A resposta da tela de entrada (2a) chega pelo estado da navegação, e não pela URL: é uma
   * escolha que só vale para esta abertura da tela. Recarregar a página a perde — e o certo é
   * perdê-la, porque aí o cartão volta a abrir no primeiro buraco que falta.
   */
  const startAtHole = (useLocation().state as { startAtHole?: number } | null)?.startAtHole;

  const [step, setStep] = useState<Step>(1);
  const [courseId, setCourseId] = useState<string | null>(null);
  const [config, setConfig] = useState<RoundConfig | null>(null);
  const [roundId, setRoundId] = useState<string | null>(null);
  const [groupRoundId, setGroupRoundId] = useState<string | null>(null);

  const existing = useMemo(
    () => inProgressRounds(allRounds.data?.rounds ?? [])[0],
    [allRounds.data],
  );

  /**
   * Resume rather than start over. The API happily creates a second in-progress round, but
   * a player who reopens this screen mid-round means "take me back to where I was", and a
   * stray duplicate would then sit in their list forever asking to be closed.
   */
  useEffect(() => {
    if (roundId || !existing) return;
    setRoundId(existing.id);
    setCourseId(existing.courseId);
    setStep(4);
  }, [existing, roundId]);

  // Ao retomar uma volta, é aqui que se descobre se ela era de flight — a volta guarda o vínculo,
  // não a tela. `null` é resposta legítima e quer dizer "era individual".
  const resumedGroup = useGroupRoundByRound(roundId && !groupRoundId ? roundId : undefined);
  useEffect(() => {
    if (resumedGroup.data && !groupRoundId) setGroupRoundId(resumedGroup.data.id);
  }, [resumedGroup.data, groupRoundId]);

  if (allRounds.isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.body}>
          <Skeleton height={18} width="50%" />
          <Skeleton height={64} style={{ marginTop: 16 }} radius={14} />
          <Skeleton height={64} style={{ marginTop: 8 }} radius={14} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {step === 1 && (
        <StepCourse
          homeCourseId={user.homeCourseId}
          courses={[...(courseIndex.data?.values() ?? [])]}
          selectedId={courseId}
          onSelect={setCourseId}
          onNext={() => setStep(2)}
          onCancel={() => navigate(routes.home)}
        />
      )}

      {step === 2 && courseId && (
        <StepSetup
          courseId={courseId}
          onBack={() => setStep(1)}
          onConfigured={(next) => {
            setConfig(next);
            setStep(3);
          }}
        />
      )}

      {step === 3 && config && (
        <StepPlayers
          config={config}
          header={<WizardHeader step={3} title="" onBack={() => setStep(2)} />}
          onStarted={(started) => {
            setRoundId(started.roundId);
            setGroupRoundId(started.groupRoundId);
            void queryClient.invalidateQueries({ queryKey: qk.allRounds });
            setStep(4);
          }}
        />
      )}

      {step === 4 && roundId && (
        <StepScorecard
          roundId={roundId}
          groupRoundId={groupRoundId}
          initialHole={startAtHole}
          onBack={() => setStep(existing ? 1 : 3)}
          onReview={() => setStep(5)}
        />
      )}

      {step === 5 && roundId && (
        <StepReview
          roundId={roundId}
          groupRoundId={groupRoundId}
          onBack={() => setStep(4)}
          onDone={() => {
            void queryClient.invalidateQueries({ queryKey: qk.allRounds });
            void queryClient.invalidateQueries({ queryKey: qk.handicap });
            // A própria volta também: o detalhe abre logo em seguida e, sem isto, serve a cópia
            // em andamento do cache por até um minuto — oferecendo "Finalizar" numa volta que
            // acabou de ser finalizada.
            void queryClient.invalidateQueries({ queryKey: qk.round(roundId) });
            if (groupRoundId) {
              void queryClient.invalidateQueries({ queryKey: qk.groupRound(groupRoundId) });
              void queryClient.invalidateQueries({ queryKey: qk.groupRoundByRound(roundId) });
            }
            navigate(routes.round(roundId), { replace: true });
          }}
          onAbandoned={() => {
            void queryClient.invalidateQueries({ queryKey: qk.allRounds });
            toast.success(t('round.abandonedToast'));
            navigate(routes.rounds, { replace: true });
          }}
        />
      )}
    </div>
  );
}

function WizardHeader({
  step,
  title,
  onBack,
  backLabel = t('rounds.back'),
  right,
}: {
  step: Step;
  title: string;
  onBack: () => void;
  backLabel?: string;
  right?: React.ReactNode;
}) {
  return (
    <header className={styles.header}>
      <div className={styles.headerTop}>
        <button type="button" className={styles.headerIcon} onClick={onBack} aria-label={backLabel}>
          {step === 1 ? <CloseIcon size={18} /> : <ChevronLeftIcon size={22} />}
        </button>
        <span className={styles.stepLabel}>
          {t('record.step', { step, total: TOTAL_STEPS })}
        </span>
        <span className={styles.headerIcon}>{right}</span>
      </div>
      <div className={styles.progress} aria-hidden="true">
        {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((i) => (
          <span key={i} className={cx(styles.progressSeg, i <= step && styles.progressSegDone)} />
        ))}
      </div>
      {/* Step 4 supplies its own hole header below, so an empty title must not reserve space. */}
      {title !== '' && <h1 className={styles.headerTitle}>{title}</h1>}
    </header>
  );
}

// ------------------------------------------------------- step 1: campo

/**
 * Quantos campos a lista mostra antes de pedir "mostrar mais".
 *
 * Quinze cabe em pouco mais de uma tela e cobre o caso real: quem tem GPS vê os mais próximos, e
 * quem não tem quase sempre procura pelo nome.
 */
const INITIAL_COURSES = 15;

function StepCourse({
  homeCourseId,
  courses,
  selectedId,
  onSelect,
  onNext,
  onCancel,
}: {
  homeCourseId: string | null;
  courses: CourseSummary[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onNext: () => void;
  onCancel: () => void;
}) {
  const [query, setQuery] = useState('');
  const geo = useGeolocation();

  const home = courses.find((c) => c.id === homeCourseId);

  /**
   * `GET /courses/nearby` sorts by distance but does not return one, and there is no
   * text-search endpoint at all — so both the search and the "km" figures are computed here
   * over the cached course list.
   */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const withDistance = courses.map((c) => ({
      course: c,
      distance:
        geo.status === 'granted'
          ? haversineMeters(geo.position, fromCoordinates(c.location.coordinates))
          : null,
    }));

    const matching = q
      ? withDistance.filter(
          ({ course }) =>
            course.name.toLowerCase().includes(q) || (course.city ?? '').toLowerCase().includes(q),
        )
      : withDistance;

    return matching
      .filter(({ course }) => course.id !== homeCourseId)
      .sort((a, b) => {
        if (a.distance != null && b.distance != null) return a.distance - b.distance;
        return collator.compare(a.course.name, b.course.name);
      });
  }, [courses, query, geo, homeCourseId]);

  /**
   * A lista completa é longa — 85 campos em produção, e crescendo — e mostrá-la inteira fazia o
   * passo virar 6 telas de rolagem. Mostrar as primeiras e oferecer o resto.
   *
   * **A busca ignora o limite de propósito:** quem digitou um nome está procurando exatamente o
   * que o corte esconderia.
   */
  const [showAll, setShowAll] = useState(false);
  const searching = query.trim().length > 0;
  const visible = searching || showAll ? filtered : filtered.slice(0, INITIAL_COURSES);
  const hiddenCount = filtered.length - visible.length;

  const selected = selectedId ? (courses.find((c) => c.id === selectedId) ?? null) : null;

  return (
    <>
      <WizardHeader
        step={1}
        title={t('record.step1.title')}
        onBack={onCancel}
        backLabel={t('record.cancel')}
      />

      {/*
        Busca e ordenação ficam presas no topo. Antes viviam dentro da lista — a busca rolava
        embora e o "Ordenar por distância" ficava *depois* de tudo que ele reordenaria, o que é
        causa e efeito invertidos: quem rolou até ele já não precisa dele.

        Os rótulos de grupo ficaram de fora daqui e continuam rolando junto do que nomeiam;
        fixo, "Próximos de você" acabaria encabeçando o bloco "Meu campo".
      */}
      <div className={styles.stickyTop}>
        <div className={styles.search}>
          <SearchIcon size={18} className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder={t('courses.searchPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={t('courses.searchAria')}
          />
        </div>

        {geo.status !== 'granted' && geo.status !== 'denied' && geo.status !== 'unavailable' && (
          <div className={styles.controlRow}>
            <Button
              size="sm"
              variant="secondary"
              busy={geo.status === 'asking'}
              onClick={geo.request}
            >
              {t('record.sortByDistance')}
            </Button>
          </div>
        )}
      </div>

      <div className={`${styles.body} ${styles.courseBody}`}>
        {/*
          A promessa sobre a localização ("lida uma única vez, recusar não bloqueia o fluxo") é
          anotação de design, não texto de tela: no canvas ela vive ao lado do quadro, não dentro
          dele. O contrato continua valendo — está em `lib/geo/useGeolocation.ts` — e o botão
          "Ordenar por distância" já diz o que vai acontecer.

          O aviso de recusa **fica**: aí não é explicação, é o estado atual da lista.
        */}
        {(geo.status === 'denied' || geo.status === 'unavailable') && (
          <p className={styles.hint}>{t('record.noLocation')}</p>
        )}

        {home && !searching && (
          <>
            <div className={styles.groupLabel}>{t('record.myCourse')}</div>
            <CourseOption
              course={home}
              selected={selectedId === home.id}
              onSelect={() => onSelect(home.id)}
            />
          </>
        )}

        <div className={styles.groupLabel}>
          {geo.status === 'granted'
            ? t('record.nearYou')
            : searching
              ? t('record.results')
              : t('rounds.filter.allCourses')}
        </div>

        {filtered.length === 0 ? (
          <p className={styles.hint}>{t('record.noCourseFor', { query })}</p>
        ) : (
          visible.map(({ course, distance }) => (
            <CourseOption
              key={course.id}
              course={course}
              distance={distance}
              selected={selectedId === course.id}
              onSelect={() => onSelect(course.id)}
            />
          ))
        )}

        {hiddenCount > 0 && (
          <div className={styles.showMore}>
            <Button block variant="secondary" onClick={() => setShowAll(true)}>
              {t('record.showMore', { count: hiddenCount })}
            </Button>
          </div>
        )}
      </div>

      {/*
        Só existe quando há escolha: sem seleção não há nada para continuar, e uma barra vazia
        roubaria o rodapé à toa. Mostra o nome escolhido porque, numa lista longa, é o único
        ponto em que dá para conferir que o toque acertou o campo certo.
      */}
      {selected && (
        <div className={styles.actionBar}>
          <div className={styles.actionBarCourse}>
            <span className={styles.actionBarLabel}>{t('record.courseLabel')}</span>
            <span className={styles.actionBarName}>{selected.name}</span>
          </div>
          <Button size="lg" onClick={onNext}>
            {t('rounds.continue')}
          </Button>
        </div>
      )}
    </>
  );
}

function CourseOption({
  course,
  distance,
  selected,
  onSelect,
}: {
  course: CourseSummary;
  distance?: number | null;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={cx(styles.courseOption, selected && styles.courseOptionSelected)}
      onClick={onSelect}
      aria-pressed={selected}
    >
      <span className={styles.courseOptionBody}>
        <span className={styles.courseOptionName}>{course.name}</span>
        <span className={styles.courseOptionMeta}>
          {[
            course.city,
            t('course.holes', { count: course.totalHoles }),
            t('course.par', { value: course.parTotal }),
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
      {distance != null && (
        <span className={styles.courseOptionRight}>{fmtCourseDistance(distance)}</span>
      )}
      {selected && (
        <span className={styles.check} aria-hidden="true">
          <CheckIcon size={16} />
        </span>
      )}
    </button>
  );
}

// ------------------------------------------------- step 2: configuração

function StepSetup({
  courseId,
  onBack,
  onConfigured,
}: {
  courseId: string;
  onBack: () => void;
  onConfigured: (config: RoundConfig) => void;
}) {
  const course = useCourse(courseId);
  const unit = loadUnit();

  const [holes, setHoles] = useState<9 | 18>(18);
  const [tee, setTee] = useState<string | null>(null);
  const [teeBack, setTeeBack] = useState<string | null>(null);

  const data = course.data;

  useEffect(() => {
    if (!data) return;
    setHoles(data.totalHoles);
    setTee((t) => t ?? data.teeBoxes[0]?.name ?? null);
  }, [data]);

  if (course.isLoading || !data) {
    return (
      <>
        <WizardHeader step={2} title={t('record.loadingCourse')} onBack={onBack} />
        <div className={styles.body}>
          <Skeleton height={40} />
          <Skeleton height={40} style={{ marginTop: 12 }} />
        </div>
      </>
    );
  }

  // A 9-hole course played as 18 needs a second tee — the API rejects the round without it.
  const needsBackTee = holes > data.totalHoles;
  const selectedTee = data.teeBoxes.find((t) => t.name === tee);
  const canStart = Boolean(tee) && (!needsBackTee || Boolean(teeBack));

  /**
   * A volta **não** nasce aqui. Quem a cria é o passo seguinte, que é quem sabe se ela é
   * individual ou de flight — e são dois endpoints diferentes. Criar antes obrigaria a apagar
   * ou remendar a volta caso o jogador acrescentasse alguém na tela seguinte.
   */
  const goToPlayers = () => {
    if (!tee) return;
    onConfigured({
      courseId,
      teeBoxName: tee,
      teeBoxNameBack: needsBackTee && teeBack ? teeBack : null,
      holesPlayed: holes,
    });
  };

  return (
    <>
      <WizardHeader step={2} title={data.name} onBack={onBack} />
      <div className={styles.body}>
        <div className={styles.groupLabel}>{t('round.holes')}</div>
        <div className={styles.halfPills}>
          {([9, 18] as const).map((h) => (
            <Pill key={h} selected={holes === h} onClick={() => setHoles(h)}>
              {h}
            </Pill>
          ))}
        </div>

        <div className={styles.groupLabel}>
          {needsBackTee ? t('record.firstNine') : t('course.tee')}
        </div>
        <TeePicker teeBoxes={data.teeBoxes} value={tee} onChange={setTee} />

        {/*
          Os dois seletores explícitos são a decisão de design; a explicação de *por que* eles
          existem (a API exige dois tees numa volta dupla) é anotação e saiu da tela. Os rótulos
          "Primeira volta" / "Segunda volta" já dizem o que cada um faz.
        */}
        {needsBackTee && (
          <>
            <div className={styles.groupLabel}>{t('record.secondNine')}</div>
            <TeePicker teeBoxes={data.teeBoxes} value={teeBack} onChange={setTeeBack} />
          </>
        )}

        {selectedTee && (
          <StatRow className={styles.teeFacts}>
            <StatBlock
              label={t('scorecard.distance')}
              value={fmtDistance(selectedTee.totalDistance, unit)}
            />
            <StatBlock label={t('round.courseRating')} value={fmtDecimal(selectedTee.courseRating)} />
            <StatBlock label={t('round.slope')} value={fmtInt(selectedTee.slopeRating)} />
          </StatRow>
        )}

        <div style={{ marginTop: '1.25rem' }}>
          <Button block size="lg" disabled={!canStart} onClick={goToPlayers}>
            {t('rounds.continue')}
          </Button>
        </div>
      </div>
    </>
  );
}

/** Exportado porque a tela de entrada no flight (2a) faz a mesma pergunta pelo mesmo motivo. */
export function TeePicker({
  teeBoxes,
  value,
  onChange,
}: {
  teeBoxes: TeeBox[];
  value: string | null;
  onChange: (name: string) => void;
}) {
  return (
    <div className={styles.pills}>
      {teeBoxes.map((t) => (
        <Pill
          key={t.name}
          selected={value === t.name}
          dotColor={teeColor(t)}
          onClick={() => onChange(t.name)}
        >
          {t.name}
        </Pill>
      ))}
    </div>
  );
}

// -------------------------------------------------- step 4: scorecard

function StepScorecard({
  roundId,
  groupRoundId,
  initialHole,
  onBack,
  onReview,
}: {
  roundId: string;
  groupRoundId: string | null;
  initialHole?: number;
  onBack: () => void;
  onReview: () => void;
}) {
  const round = useRound(roundId);
  const course = useCourse(round.data?.courseId);

  if (round.isLoading || !round.data) {
    return (
      <>
        <WizardHeader step={4} title={t('record.loadingRound')} onBack={onBack} />
        <div className={styles.body}>
          <Skeleton height={120} radius={14} />
        </div>
      </>
    );
  }

  // Duas telas para o mesmo passo: com flight, o cartão do grupo (1s); sozinho, o de sempre.
  // A escolha é do dado, não de uma preferência — `groupRoundId` só existe se alguém entrou.
  if (groupRoundId) {
    return (
      <GroupScorecard
        groupRoundId={groupRoundId}
        initialHole={initialHole}
        header={(right) => <WizardHeader step={4} title="" onBack={onBack} right={right} />}
        onReview={onReview}
      />
    );
  }

  // `title=""` is deliberate: the hole header (number, par, SI, distance, running total) is
  // richer than any static string, and belongs to HoleEntry — which renders it directly
  // under this bar.
  return (
    <HoleEntry
      round={round.data}
      course={course.data}
      header={(right) => <WizardHeader step={4} title="" onBack={onBack} right={right} />}
      onReview={onReview}
    />
  );
}

// ---------------------------------------------------- step 5: revisar

function StepReview({
  roundId,
  groupRoundId,
  onBack,
  onDone,
  onAbandoned,
}: {
  roundId: string;
  groupRoundId: string | null;
  onBack: () => void;
  onDone: () => void;
  onAbandoned: () => void;
}) {
  const round = useRound(roundId);
  const courseIndex = useCourseIndex();
  const group = useGroupRound(groupRoundId ?? undefined);
  const toast = useToast();
  const queryClient = useQueryClient();

  const [working, setWorking] = useState(false);
  const [confirmAbandon, setConfirmAbandon] = useState(false);

  /*
   * Fechar o **flight** é do marcador: é a volta dele que dá status ao cartão, e é o fechamento
   * dela que arrasta as dos outros. Quem entrou fecha só a própria — a linha dele já está gravada
   * e o flight continua aberto até o marcador encerrar. Sem esta distinção, quem entrou apertava
   * "Finalizar" e levava um 403 do servidor, que recusa quem não é dono.
   */
  const closesTheFlight = Boolean(groupRoundId) && (group.data?.viewer.isOwner ?? false);

  const data = round.data;
  /*
   * A espera pelo flight faz parte da tela, e não é cerimônia: `closesTheFlight` sai de
   * `group.data`, e decidir antes dela chegar faria o marcador fechar só a própria volta —
   * deixando as dos outros abertas sem ninguém perceber. Na prática o dado já vem do cache do
   * passo anterior; isto é o que garante que continue verdade se aquele passo mudar.
   */
  if (round.isLoading || !data || (groupRoundId !== null && group.isLoading)) {
    return (
      <>
        <WizardHeader step={5} title={t('record.review')} onBack={onBack} />
        <div className={styles.body}>
          <Skeleton height={80} />
        </div>
      </>
    );
  }

  const scored = data.stats.holesScored;
  const missing = data.holesPlayed - scored;
  // The server requires an exact match; see closurePlan in the rounds list.
  const canFinish = missing === 0;
  const canCloseAsNine = scored === 9 && data.holesPlayed > 9;
  const firstMissing = findFirstMissing(data);

  const finish = async (asHolesPlayed?: 9 | 18) => {
    setWorking(true);
    try {
      const before = data.playerHandicapAtStart;
      // O endpoint de grupo delega ao mesmo caso de uso da volta individual e ainda devolve o
      // quadro final — por isso o marcador passa por ele, e só ele.
      if (closesTheFlight && groupRoundId) await groupRoundsApi.complete(groupRoundId, asHolesPlayed);
      else await roundsApi.complete(data.id, asHolesPlayed);
      const after = await queryClient.fetchQuery({
        queryKey: qk.handicap,
        queryFn: usersApi.handicap,
      });
      toast.success(
        before != null && after.handicapIndex != null
          ? t('round.finishedWithHandicap', {
              before: fmtDecimal(before),
              after: fmtDecimal(after.handicapIndex),
            })
          : t('round.finishedRecalculated'),
      );
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('round.finishFailed'));
    } finally {
      setWorking(false);
    }
  };

  const abandon = async () => {
    setWorking(true);
    try {
      await roundsApi.abandon(data.id);
      onAbandoned();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('round.abandonFailed'));
    } finally {
      setWorking(false);
    }
  };

  return (
    <>
      <header className={styles.header}>
        <div className={styles.headerTop}>
          <button
            type="button"
            className={styles.headerIcon}
            onClick={onBack}
            aria-label={t('rounds.back')}
          >
            <ChevronLeftIcon size={22} />
          </button>
          <span className={styles.stepLabel}>
            {t('record.step', { step: TOTAL_STEPS, total: TOTAL_STEPS })}
          </span>
          <span className={styles.headerIcon} />
        </div>
        <div className={styles.progress} aria-hidden="true">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((i) => (
            <span key={i} className={cx(styles.progressSeg, styles.progressSegDone)} />
          ))}
        </div>

        <div className={styles.reviewScore}>
          <span className={styles.reviewScoreValue}>{fmtInt(data.stats.totalStrokes)}</span>
          <span className={styles.reviewToPar}>{fmtToPar(data.stats.totalToPar)}</span>
        </div>
        <p className={styles.reviewMeta}>
          {t('record.reviewMeta', {
            scored,
            holes: data.holesPlayed,
            course: courseNameFrom(courseIndex.data, data.courseId),
          })}
        </p>
      </header>

      <StatRow className={styles.reviewStats}>
        <StatBlock label={t('round.putts')} value={fmtInt(data.stats.totalPutts)} />
        <StatBlock label={t('round.gir')} value={fmtPct(data.stats.greensInRegulationPct)} />
        <StatBlock label={t('scorecard.fw')} value={fmtPct(data.stats.fairwayHitPct)} />
      </StatRow>

      {canFinish ? (
        <div className={styles.reviewActions}>
          <div className={styles.hint}>
            {tx('record.finishWarning', {
              strong: (
                <strong style={{ color: 'var(--text)' }}>{t('record.finishWarningStrong')}</strong>
              ),
            })}
          </div>
          {/* Quem entrou no flight fecha só o próprio cartão — vale dizer isso antes do toque. */}
          {groupRoundId && !closesTheFlight && (
            <div className={styles.hint}>{t('record.finishOwnCardOnly')}</div>
          )}
          <Button block size="lg" busy={working} onClick={() => void finish()}>
            {t('round.finish')}
          </Button>
          <Button block variant="secondary" size="lg" onClick={onBack}>
            {t('record.keepEditing')}
          </Button>
          <Button variant="danger" block onClick={() => setConfirmAbandon(true)}>
            {t('round.abandon')}
          </Button>
        </div>
      ) : (
        <div className={styles.blockPanel}>
          <div className={styles.blockLabel}>
            {t('record.apiBlock', { scored, holes: data.holesPlayed })}
          </div>
          <div
            style={{
              background: 'var(--danger-wash)',
              borderRadius: 'var(--radius-md)',
              padding: '0.75rem 0.8125rem',
            }}
          >
            <div style={{ fontSize: '0.78125rem', fontWeight: 600, color: 'var(--danger)' }}>
              {t('record.missing', { count: missing, first: firstMissing ?? '' })}
            </div>
            <div
              style={{
                fontSize: '0.71875rem',
                lineHeight: 1.5,
                color: 'var(--danger-text)',
                marginTop: 4,
              }}
            >
              {t('record.apiRule', { holes: data.holesPlayed })}
              {canCloseAsNine ? t('record.canClose9') : t('record.cannotClose9')}
            </div>
          </div>

          <div className={styles.blockActions}>
            <Button block size="lg" onClick={onBack}>
              {firstMissing
                ? t('record.goToHole', { number: firstMissing })
                : t('round.finishDialog.cancel')}
            </Button>
            {canCloseAsNine && (
              <Button
                block
                variant="secondary"
                size="lg"
                busy={working}
                onClick={() => void finish(9)}
              >
                {t('record.closeAs9Holes')}
              </Button>
            )}
            <Button variant="danger" block onClick={() => setConfirmAbandon(true)}>
              {t('round.abandon')}
            </Button>
          </div>
        </div>
      )}

      <Dialog
        open={confirmAbandon}
        onClose={() => setConfirmAbandon(false)}
        title={t('round.abandonDialog.title')}
        confirmLabel={t('round.abandonDialog.confirm')}
        onConfirm={() => setConfirmAbandon(false)}
        cancelLabel={t('rounds.back')}
        destructiveLabel={t('rounds.abandonAction')}
        onDestructive={() => void abandon()}
      >
        {t('round.abandonDialog.body', { count: scored })}
      </Dialog>
    </>
  );
}

/** The lowest hole number with no score — where "Ir para o buraco N" should land. */
function findFirstMissing(round: Round): number | null {
  const filled = new Set(round.scores.map((s) => s.holeNumber));
  for (let n = 1; n <= round.holesPlayed; n++) {
    if (!filled.has(n)) return n;
  }
  return null;
}
